import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { 
  Grid3x3,
  Plus,
  Edit,
  Trash2,
  Settings,
  Users,
  TrendingUp,
  DollarSign,
  RotateCcw,
  Eye,
  EyeOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMatrix = () => {
  const [matrices, setMatrices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingMatrix, setEditingMatrix] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    matrix_type: '1x2',
    placement_mode: 'company_forced',
    width: 2,
    depth: 10,
    entry_price: 10.0,
    auto_repurchase: true,
    phase_requirement: null,
    is_active: true
  });

  const matrixTypes = [
    { value: '1x2', label: '1×2 Matrix', description: 'Two positions wide' },
    { value: '12x3', label: '12×3 Matrix', description: 'Twelve positions wide, three levels deep' }
  ];

  const placementModes = [
    { value: 'company_forced', label: 'Company Forced', description: 'Global queue placement' },
    { value: 'follow_sponsor', label: 'Follow Sponsor', description: 'Place under sponsor lineage' },
    { value: 'hybrid', label: 'Hybrid', description: 'Try sponsor first, fallback to company' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Note: These endpoints would need to be implemented in the backend
      const [matricesRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/matrices`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/products`).catch(() => ({ data: [] }))
      ]);
      
      setMatrices(matricesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load matrix data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingMatrix) {
        await axios.put(`${API}/admin/matrices/${editingMatrix.id}`, formData);
        toast.success('Matrix updated successfully');
      } else {
        await axios.post(`${API}/admin/matrices`, formData);
        toast.success('Matrix created successfully');
      }
      
      setShowDialog(false);
      setEditingMatrix(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save matrix:', error);
      const message = error.response?.data?.detail || 'Failed to save matrix';
      toast.error(message);
    }
  };

  const handleEdit = (matrix) => {
    setEditingMatrix(matrix);
    setFormData({ ...matrix });
    setShowDialog(true);
  };

  const handleDelete = async (matrixId) => {
    if (!window.confirm('Are you sure you want to delete this matrix? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/matrices/${matrixId}`);
      toast.success('Matrix deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete matrix:', error);
      toast.error('Failed to delete matrix');
    }
  };

  const toggleMatrixStatus = async (matrixId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/matrices/${matrixId}/status`, {
        is_active: !currentStatus
      });
      toast.success(`Matrix ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchData();
    } catch (error) {
      console.error('Failed to toggle matrix status:', error);
      toast.error('Failed to update matrix status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      matrix_type: '1x2',
      placement_mode: 'company_forced',
      width: 2,
      depth: 10,
      entry_price: 10.0,
      auto_repurchase: true,
      phase_requirement: null,
      is_active: true
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Matrix Management">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Matrix Management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Compensation Matrices</h2>
          <p className="text-sm text-gray-600">
            Configure and manage matrix compensation systems
          </p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            resetForm();
            setEditingMatrix(null);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Add Matrix
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingMatrix ? 'Edit Matrix' : 'Create New Matrix'}
              </DialogTitle>
              <DialogDescription>
                Configure the matrix compensation system parameters
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Matrix Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Basic 1x2 Matrix"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="matrix_type">Matrix Type</Label>
                <Select 
                  value={formData.matrix_type} 
                  onValueChange={(value) => {
                    handleInputChange('matrix_type', value);
                    // Auto-set width based on type
                    if (value === '1x2') {
                      handleInputChange('width', 2);
                      handleInputChange('depth', 10);
                    } else if (value === '12x3') {
                      handleInputChange('width', 12);
                      handleInputChange('depth', 3);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select matrix type" />
                  </SelectTrigger>
                  <SelectContent>
                    {matrixTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-xs text-gray-500">{type.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="placement_mode">Placement Mode</Label>
                <Select 
                  value={formData.placement_mode} 
                  onValueChange={(value) => handleInputChange('placement_mode', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select placement mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {placementModes.map((mode) => (
                      <SelectItem key={mode.value} value={mode.value}>
                        <div>
                          <div className="font-medium">{mode.label}</div>
                          <div className="text-xs text-gray-500">{mode.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="width">Width</Label>
                  <Input
                    id="width"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.width}
                    onChange={(e) => handleInputChange('width', parseInt(e.target.value))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="depth">Max Depth</Label>
                  <Input
                    id="depth"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.depth}
                    onChange={(e) => handleInputChange('depth', parseInt(e.target.value))}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="entry_price">Entry Price ($)</Label>
                <Input
                  id="entry_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.entry_price}
                  onChange={(e) => handleInputChange('entry_price', parseFloat(e.target.value))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phase_requirement">Phase Requirement (PIF Count)</Label>
                <Input
                  id="phase_requirement"
                  type="number"
                  min="0"
                  placeholder="Leave empty for Phase 1 (no requirement)"
                  value={formData.phase_requirement || ''}
                  onChange={(e) => handleInputChange('phase_requirement', e.target.value ? parseInt(e.target.value) : null)}
                />
                <p className="text-xs text-gray-500">
                  Number of PIF completions required to access this matrix
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="auto_repurchase"
                  type="checkbox"
                  checked={formData.auto_repurchase}
                  onChange={(e) => handleInputChange('auto_repurchase', e.target.checked)}
                  className="h-4 w-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                />
                <Label htmlFor="auto_repurchase" className="text-sm">
                  Enable Auto-Repurchase
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="h-4 w-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                />
                <Label htmlFor="is_active" className="text-sm">
                  Active
                </Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary">
                  {editingMatrix ? 'Update Matrix' : 'Create Matrix'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Matrix Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {matrices.length > 0 ? matrices.map((matrix) => (
          <Card key={matrix.id} className={`border-2 ${matrix.is_active ? 'border-green-200' : 'border-gray-200'}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Grid3x3 className="h-5 w-5 text-yellow-600 mr-2" />
                  <div>
                    <CardTitle className="text-lg">{matrix.name}</CardTitle>
                    <CardDescription>
                      {matrix.matrix_type} • {matrix.placement_mode.replace('_', ' ')}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant={matrix.is_active ? "secondary" : "outline"}>
                    {matrix.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Size:</span>
                    <div className="font-medium">{matrix.width}×{matrix.depth}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Entry Price:</span>
                    <div className="font-medium">${matrix.entry_price}</div>
                  </div>
                </div>
                
                {matrix.phase_requirement && (
                  <div className="text-sm">
                    <span className="text-gray-600">Phase Requirement:</span>
                    <div className="font-medium">{matrix.phase_requirement} PIF completions</div>
                  </div>
                )}
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <RotateCcw className="h-4 w-4 mr-1 text-gray-600" />
                    <span>Auto-Repurchase: {matrix.auto_repurchase ? 'Yes' : 'No'}</span>
                  </div>
                </div>
                
                <div className="flex space-x-2 pt-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(matrix)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleMatrixStatus(matrix.id, matrix.is_active)}
                  >
                    {matrix.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(matrix.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card className="col-span-full">
            <CardContent className="text-center py-8">
              <Grid3x3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Matrices Configured</h3>
              <p className="text-gray-600 mb-4">
                Create your first compensation matrix to get started
              </p>
              <Button className="btn-primary" onClick={() => setShowDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Matrix
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Matrix Stats */}
      {matrices.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Total Matrices
              </CardTitle>
              <Grid3x3 className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{matrices.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Active Matrices
              </CardTitle>
              <Eye className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {matrices.filter(m => m.is_active).length}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Auto-Repurchase
              </CardTitle>
              <RotateCcw className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {matrices.filter(m => m.auto_repurchase).length}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Total Entry Value
              </CardTitle>
              <DollarSign className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                ${matrices.reduce((sum, m) => sum + m.entry_price, 0).toFixed(2)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminMatrix;